import moment from 'moment';
import * as d3 from 'd3';
import * as fc from 'd3fc';
import './d3Chart.scss';

class d3Chart {
    constructor(el, data) {
        this.el = d3.select(el);
        this.data = data;
        this.trackball = {
            date: null,
            posted: null,
            calc: null
        };

        this.setTrackball = (date) => {
            if (!date) {
                this.trackball = {
                    date: null,
                    posted: null,
                    calc: null
                };
                return;
            }
            const { calc, posted } = this.data.find(d => d.date === date);
            this.trackball = {
                date,
                posted,
                calc
            };
        };

        this.findClosestDate = (dateRaw) => {
            const date = moment(dateRaw);
            let lowestInterval;
            let closestDate;

            // eslint-disable-next-line no-restricted-syntax
            for (const d of this.data) {
                const dateInterval = Math.abs(date.diff(d.date));

                if (!lowestInterval || dateInterval < lowestInterval) {
                    lowestInterval = dateInterval;
                    closestDate = d.date;
                }
            }

            return closestDate;
        };

        this.xScale = d3.scaleTime();
        this.yScale = d3.scaleLinear();

        const myCustomAxis = scale => fc.axisLabelRotate(
            fc.axisBottom(scale)
                // .tickArguments(data.map(d => d.date))
                // .ticks(data.length)
                .tickFormat(d => moment(d).format('DD-MMM-YY'))
        );

        this.chart = fc.chartCartesian({
            xScale: this.xScale,
            yScale: this.yScale,
            xAxis: {
                bottom: myCustomAxis
            },
            yAxis: {
                left: fc.axisLeft
            }
        })
            .xLabel('Date')
            .yLabel('Amount')
            .yOrient('left')
            .decorate((selection) => {
                const updateHeight = selection.select('.x-label')
                    .style('margin-top', '30px')
                    .style('margin-bottom', '10px');

                selection.enter()
                    .merge(updateHeight);

                const updateWidth = selection.select('.y-label')
                    .style('margin-right', '30px')
                    .style('margin-left', '10px');

                selection.enter()
                    .merge(updateWidth);
            });

        const lineA = fc.seriesSvgLine()
            .crossValue(d => d.date)
            .mainValue(d => d.calc)
            .decorate((selection) => {
                selection.enter().classed('lineA', true);
            });

        const pointA = fc.seriesSvgPoint()
            .crossValue(d => d.date)
            .mainValue(d => d.calc)
            .size(25)
            .decorate((selection) => {
                selection.enter()
                    .classed('tooltipPointA', true)
                    .append('text')
                    .classed('tooltipA', true);
                selection.select('text')
                    .text(d => d.calc.toLocaleString());
            });

        const lineB = fc.seriesSvgLine()
            .crossValue(d => d.date)
            .mainValue(d => d.posted)
            .decorate((selection) => {
                selection.enter().classed('lineB', true);
            });

        const pointB = fc.seriesSvgPoint()
            .crossValue(d => d.date)
            .mainValue(d => d.posted)
            .size(25)
            .decorate((selection) => {
                selection.enter()
                    .classed('tooltipPointB', true)
                    .append('text')
                    .classed('tooltipB', true);
                selection.select('text')
                    .text(d => d.posted.toLocaleString());
            });

        const gridlines = fc.annotationSvgGridline();
        // .xTicks(data.length);

        const multi = fc.seriesSvgMulti()
            .series([gridlines, lineA, lineB, pointA, pointB])
            .mapping((data, index, series) => {
                switch (series[index]) {
                    case pointA:
                    case pointB:
                        return [this.trackball];
                    default:
                        return data;
                }
            });


        this.chart.svgPlotArea(multi);
    }

    render(data = this.data) {
        if (data.length > 0) {
            const xDomain = fc.extentTime()
                .accessors([d => d.date])
                .pad([0.05, 0.15])(data);
            this.chart
                .xDomain(
                    xDomain
                )
                .yDomain(
                    fc.extentLinear()
                        .accessors([d => d.posted, d => d.calc])
                        .pad([0.1, 0.1])(data)
                );
        }

        this.data = data;

        this.el
            .datum(data)
            .call(this.chart);

        const pointer = fc.pointer()
            .on('point', (event) => {
                if (event.length) {
                    const { x } = event[0];
                    const date = this.xScale.invert(x);
                    this.setTrackball(this.findClosestDate(date));
                    this.render(data);
                } else {
                    this.setTrackball();
                }
            });

        this.el.select('.plot-area').call(pointer);
    }
}

export default d3Chart;
